import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPhone, 
  faEnvelope, 
  faClock, 
  faPaperPlane, 
  faHeadset,
  faCheckCircle,
  faShoppingBag,
  faTag
} from '@fortawesome/free-solid-svg-icons';
import { faInstagram as brandInstagram, faWhatsapp as brandWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { useAuth } from '../context/AuthContext';
import emailjs from '@emailjs/browser';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';

// EmailJS'i başlat
emailjs.init("ARIvWisrsFo4g0Z8A");

function Contact() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  // Animasyon varyantları
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  useEffect(() => {
    if (user) {
      setFormData(prevState => ({
        ...prevState,
        fullName: `${user.first_name} ${user.last_name}`,
        email: user.email,
        phone: user.phone || ''
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const formatPhoneNumber = (phone) => {
    // Remove non-numeric characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Format as +90 XXX XXX XX XX
    if (cleaned.length === 10) {
      return `+90 ${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8)}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('0')) {
      return `+90 ${cleaned.slice(1, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7, 9)} ${cleaned.slice(9)}`;
    }
    return phone; // Return original if format doesn't match
  };

  const capitalizeFirstLetter = (string) => {
    return string.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const templateParams = {
        from_name: capitalizeFirstLetter(formData.fullName),
        from_email: formData.email.toLowerCase(),
        phone: formatPhoneNumber(formData.phone),
        subject: capitalizeFirstLetter(formData.subject),
        message: formData.message,
        to_email: 'mr.browncases@gmail.com'
      };

      await emailjs.send(
        'service_u4gv3zu',
        'template_ntgia2j',
        templateParams,
        'ARIvWisrsFo4g0Z8A'
      );

      toast.success('Mesajınız başarıyla gönderildi!');
      setIsSubmitted(true);
      
      // Form'u temizle
      setFormData({
        fullName: user ? `${user.first_name} ${user.last_name}` : '',
        email: user ? user.email : '',
        phone: user ? user.phone || '' : '',
        subject: '',
        message: ''
      });

      // 5 saniye sonra submitted durumunu sıfırla
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Mesaj gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page position-relative overflow-hidden">
      {/* Hero Banner */}
      <div className="contact-hero position-relative mb-5">
        <div className="contact-hero-bg" style={{
          background: 'linear-gradient(135deg, #F5F5DC 0%, #FFF8DC 50%, #FAEBD7 100%)',
          height: '300px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}>
          {/* Dekoratif Şekiller */}
          <div className="position-absolute rounded-circle" 
            style={{ 
              top: '20%', 
              left: '10%', 
              width: '120px', 
              height: '120px', 
              backgroundColor: '#8B4513', 
              opacity: 0.2,
              animation: 'blob 7s infinite'
            }}>
          </div>
          <div className="position-absolute rounded-circle" 
            style={{ 
              bottom: '10%', 
              right: '15%', 
              width: '150px', 
              height: '150px', 
              backgroundColor: '#A0522D', 
              opacity: 0.15,
              animation: 'blob 7s infinite 2s'
            }}>
          </div>
          
          <Container className="h-100 d-flex flex-column justify-content-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center text-brown"
            >
              <h1 className="fw-bold">Bize Ulaşın</h1>
              <p className="fs-5 mt-3 mb-0">Sorularınız, önerileriniz veya işbirliği teklifleriniz için iletişime geçin</p>
            </motion.div>
          </Container>
        </div>
      </div>
      
      <Container className="py-5 page-transition position-relative" style={{ zIndex: 10 }}>
        <Row className="g-4">
          <Col lg={5} className="mb-4 mb-lg-0">
            <motion.div 
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="h-100 d-flex flex-column"
            >
              <motion.div variants={fadeInUp} className="contact-info-card mb-4 bg-white rounded-lg shadow-sm border-0 p-4">
                <h3 className="fw-bold mb-4 text-brown">İletişim Bilgileri</h3>
                
                <div className="contact-item d-flex align-items-start mb-4">
                  <div className="contact-icon me-3">
                    <div className="rounded-circle bg-brown-50 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                      <FontAwesomeIcon icon={faEnvelope} className="text-brown fs-4" />
                    </div>
                  </div>
                  <div>
                    <h5 className="mb-2 fw-semibold">E-posta</h5>
                    <p className="mb-0 text-muted">mr.browncases@gmail.com</p>
                  </div>
                </div>
                
                <div className="contact-item d-flex align-items-start mb-4">
                  <div className="contact-icon me-3">
                    <div className="rounded-circle bg-brown-50 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                      <FontAwesomeIcon icon={faPhone} className="text-brown fs-4" />
                    </div>
                  </div>
                  <div>
                    <h5 className="mb-2 fw-semibold">Telefon</h5>
                    <p className="mb-0 text-muted">+90 535 873 46 92</p>
                  </div>
                </div>
                
                <div className="contact-item d-flex align-items-start mb-4">
                  <div className="contact-icon me-3">
                    <div className="rounded-circle bg-brown-50 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                      <FontAwesomeIcon icon={faClock} className="text-brown fs-4" />
                    </div>
                  </div>
                  <div>
                    <h5 className="mb-2 fw-semibold">Çalışma Saatleri</h5>
                    <p className="mb-1 text-muted">Pazartesi - Cuma: 09:00 - 18:00</p>
                    <p className="mb-0 text-muted">Sipariş ve destek hizmetlerimiz</p>
                  </div>
                </div>
                
                <div className="contact-item d-flex align-items-start">
                  <div className="contact-icon me-3">
                    <div className="rounded-circle bg-brown-50 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                      <FontAwesomeIcon icon={brandInstagram} className="text-brown fs-4" />
                    </div>
                  </div>
                  <div>
                    <h5 className="mb-2 fw-semibold">Sosyal Medya</h5>
                    <p className="mb-0 text-muted">
                      <a href="https://www.instagram.com/mr.browncase" target="_blank" rel="noopener noreferrer" className="text-decoration-none text-brown">
                        @mr.browncase
                      </a>
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="mb-4 bg-white rounded-lg shadow-sm border-0 p-4">
                <h3 className="fw-bold mb-4 text-brown">Online Mağazamız</h3>
                <div className="d-flex flex-column">
                  <div className="contact-item d-flex align-items-start mb-4">
                    <div className="contact-icon me-3">
                      <div className="rounded-circle bg-brown-50 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                        <FontAwesomeIcon icon={faShoppingBag} className="text-brown fs-4" />
                      </div>
                    </div>
                    <div>
                      <h5 className="mb-2 fw-semibold">Ürünlerimiz</h5>
                      <p className="mb-0 text-muted">Geniş ürün yelpazemizle telefonunuza mükemmel koruma ve stil sağlıyoruz.</p>
                    </div>
                  </div>
                  <div className="contact-item d-flex align-items-start mb-4">
                    <div className="contact-icon me-3">
                      <div className="rounded-circle bg-brown-50 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                        <FontAwesomeIcon icon={faTag} className="text-brown fs-4" />
                      </div>
                    </div>
                    <div>
                      <h5 className="mb-2 fw-semibold">Özel Teklifler</h5>
                      <p className="mb-0 text-muted">Instagram sayfamızı takip ederek en güncel kampanyalardan haberdar olun.</p>
                    </div>
                  </div>
                  <div className="contact-item d-flex align-items-start">
                    <div className="contact-icon me-3">
                      <div className="rounded-circle bg-brown-50 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                        <FontAwesomeIcon icon={brandWhatsapp} className="text-brown fs-4" />
                      </div>
                    </div>
                    <div>
                      <h5 className="mb-2 fw-semibold">WhatsApp</h5>
                      <p className="mb-0 text-muted">
                        <a href="https://wa.me/905358734692" target="_blank" rel="noopener noreferrer" className="text-decoration-none text-brown">
                          Hızlı iletişim için WhatsApp'tan yazın
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </Col>

          <Col lg={7}>
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="contact-form-card bg-white p-4 p-lg-5 rounded-lg shadow-sm border-0"
            >
              {isSubmitted ? (
                <div className="text-center py-5">
                  <div className="mb-4">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-success" style={{ fontSize: '4rem' }} />
                  </div>
                  <h3 className="mb-3 fw-bold">Teşekkürler!</h3>
                  <p className="mb-4 text-muted">Mesajınız başarıyla gönderildi. En kısa sürede size geri dönüş yapacağız.</p>
                  <Button 
                    variant="outline-brown" 
                    className="px-4"
                    onClick={() => setIsSubmitted(false)}
                  >
                    Yeni Mesaj Gönder
                  </Button>
                </div>
              ) : (
                <>
                  <div className="text-center mb-4">
                    <div className="contact-icon d-inline-block mb-3">
                      <div className="rounded-circle mx-auto bg-brown-50 d-flex align-items-center justify-content-center" style={{ width: '70px', height: '70px' }}>
                        <FontAwesomeIcon icon={faHeadset} className="text-brown" style={{ fontSize: '2rem' }} />
                      </div>
                    </div>
                    <h3 className="fw-bold mb-2">Bize Ulaşın</h3>
                    <p className="text-muted">Sorularınızı yanıtlamak için buradayız.</p>
                  </div>
                  
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Adınız Soyadınız</Form.Label>
                          <Form.Control 
                            type="text" 
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            placeholder="Adınızı ve soyadınızı giriniz"
                            className="border-0 bg-light py-2 px-3"
                            required 
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>E-posta Adresiniz</Form.Label>
                          <Form.Control 
                            type="email" 
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="E-posta adresinizi giriniz"
                            className="border-0 bg-light py-2 px-3"
                            required 
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Telefon Numaranız</Form.Label>
                          <Form.Control 
                            type="tel" 
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="Telefon numaranızı giriniz"
                            className="border-0 bg-light py-2 px-3"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Konu</Form.Label>
                          <Form.Control 
                            type="text" 
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            placeholder="Mesajınızın konusunu giriniz"
                            className="border-0 bg-light py-2 px-3"
                            required 
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-4">
                      <Form.Label>Mesajınız</Form.Label>
                      <Form.Control 
                        as="textarea" 
                        rows={5} 
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Mesajınızı giriniz"
                        className="border-0 bg-light py-2 px-3"
                        required 
                      />
                    </Form.Group>

                    <Button 
                      type="submit" 
                      className="btn-brown w-100 py-3 rounded-pill shadow-sm"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Gönderiliyor...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                          Mesaj Gönder
                        </>
                      )}
                    </Button>
                  </Form>
                </>
              )}
            </motion.div>
          </Col>
        </Row>
        
        {/* Müşteri Hizmetleri Bilgi Kartları */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <h3 className="text-center fw-bold mt-5 mb-4">Size Nasıl Yardımcı Olabiliriz?</h3>
          <Row className="g-4 mt-2">
            <Col md={4} xs={12}>
              <Card className="h-100 shadow-sm border-0 p-4 text-center service-card">
                <div className="icon-container mx-auto mb-3">
                  <div className="rounded-circle mx-auto bg-brown-50 d-flex align-items-center justify-content-center" style={{ width: '70px', height: '70px' }}>
                    <FontAwesomeIcon icon={brandInstagram} className="text-brown" style={{ fontSize: '2rem' }} />
                  </div>
                </div>
                <Card.Body>
                  <Card.Title className="fw-bold mb-3">Instagram</Card.Title>
                  <Card.Text className="text-muted">
                    En yeni ürünlerimiz ve özel kampanyalarımız için Instagram sayfamızı takip edin.
                  </Card.Text>
                  <Button 
                    variant="brown" 
                    className="mt-3 rounded-pill"
                    href="https://www.instagram.com/mr.browncase"
                    target="_blank"
                  >
                    <FontAwesomeIcon icon={brandInstagram} className="me-2" />
                    Takip Et
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} xs={12}>
              <Card className="h-100 shadow-sm border-0 p-4 text-center service-card">
                <div className="icon-container mx-auto mb-3">
                  <div className="rounded-circle mx-auto bg-brown-50 d-flex align-items-center justify-content-center" style={{ width: '70px', height: '70px' }}>
                    <FontAwesomeIcon icon={faHeadset} className="text-brown" style={{ fontSize: '2rem' }} />
                  </div>
                </div>
                <Card.Body>
                  <Card.Title className="fw-bold mb-3">Canlı Destek</Card.Title>
                  <Card.Text className="text-muted">
                    Anında yardım mı gerekiyor? Müşteri hizmetleri ekibimiz ile canlı sohbet başlatın.
                  </Card.Text>
                  <Button 
                    variant="brown" 
                    className="mt-3 rounded-pill"
                    onClick={() => {
                      // Directly click on the floating support icon
                      const floatingSupport = document.querySelector('.floating-support');
                      if (floatingSupport) {
                        floatingSupport.click();
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faHeadset} className="me-2" />
                    Canlı Destek Başlat
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} xs={12}>
              <Card className="h-100 shadow-sm border-0 p-4 text-center service-card">
                <div className="icon-container mx-auto mb-3">
                  <div className="rounded-circle mx-auto bg-brown-50 d-flex align-items-center justify-content-center" style={{ width: '70px', height: '70px' }}>
                    <FontAwesomeIcon icon={brandWhatsapp} className="text-brown" style={{ fontSize: '2rem' }} />
                  </div>
                </div>
                <Card.Body>
                  <Card.Title className="fw-bold mb-3">WhatsApp Desteği</Card.Title>
                  <Card.Text className="text-muted">
                    Hızlı yanıt almak mı istiyorsunuz? WhatsApp üzerinden bizimle iletişime geçebilirsiniz.
                  </Card.Text>
                  <Button 
                    variant="success" 
                    className="mt-3 rounded-pill"
                    href="https://wa.me/905358734692"
                    target="_blank"
                  >
                    <FontAwesomeIcon icon={brandWhatsapp} className="me-2" />
                    WhatsApp'tan Yaz
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </motion.div>
      </Container>
      
      {/* Animasyon ve Stil */}
      <style jsx>{`
        .text-brown {
          color: #8B4513;
        }
        
        .bg-brown-50 {
          background-color: rgba(139, 69, 19, 0.15);
        }
        
        .btn-brown {
          background-color: #8B4513;
          border-color: #8B4513;
          color: white;
        }
        
        .btn-brown:hover {
          background-color: #A0522D;
          border-color: #A0522D;
          color: white;
        }
        
        .btn-outline-brown {
          background-color: transparent;
          border-color: #8B4513;
          color: #8B4513;
        }
        
        .btn-outline-brown:hover {
          background-color: #8B4513;
          color: white;
        }
        
        .rounded-lg {
          border-radius: 12px !important;
        }
        
        .service-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        
        .service-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.1) !important;
        }
        
        .social-link {
          transition: transform 0.3s ease;
        }
        
        .social-link:hover {
          transform: translateY(-3px);
        }
        
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
      `}</style>
    </div>
  );
}

export default Contact; 